'use client';

import { isClientFetchError } from '@/app/client-fetcher';
import {
  APPLE_PAY_BUTTON_ID,
  GenerateAppleButtonParams,
  generateApplePayButton,
} from '@/app/components/DonationForm/donationFormParts/ApplePaySubmitButton/generate-apple-pay-button';
import { loadTrustPayScript } from '@/app/components/DonationForm/donationFormParts/ApplePaySubmitButton/load-trust-pay-script';
import { useBackendValidation } from '@/app/components/DonationForm/donationFormParts/ApplePaySubmitButton/use-beckend-validation';
import { useCreatePaymentIntent } from '@/app/components/DonationForm/donationFormParts/ApplePaySubmitButton/use-create-payment-intent';
import { useHandlePaymentProcessed } from '@/app/components/DonationForm/donationFormParts/ApplePaySubmitButton/use-handle-payment-processed';
import { DonationFormBaseValues } from '@/app/components/DonationForm/types';
import { transformCreateDonationViolationsToDonationFormErrors } from '@/app/components/DonationForm/utils';
import { Language } from '@/app/enums';
import { useDebouncedEffect } from '@/app/hooks/useDebouncedEffect';
import { getPriceInCents } from '@/app/utils/numbers';
import { ApiRequestValidationExceptionResponse } from '@/src/api/models';
import { CreateDonationRequest } from '@/src/api/models/CreateDonationRequest';
import { captureException } from '@sentry/nextjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const buttonLocaleResolver: Record<Language, string> = {
  [Language.CZ]: 'cs-CZ',
  [Language.SK]: 'sk-SK',
  [Language.EN]: 'en-US',
};

type ApplePaySubmitButtonProps = {
  totalAmount: number;
  projectId: string;
  submitForm: () => void;
  isFormValid: boolean;
  isPlus?: boolean;
  isSubmitting: boolean;
  onPaymentProcessError: (message: string) => void;
  onGenerateAppleButtonError: (message: string) => void;
  transformedValuesForDonationRequest: (values: DonationFormBaseValues) => CreateDonationRequest;
  formValues: DonationFormBaseValues;
  setErrors: (errors: Record<string, string>) => void;
};

export const ApplePaySubmitButton = ({
  totalAmount,
  projectId,
  isFormValid,
  submitForm,
  isPlus,
  onPaymentProcessError,
  onGenerateAppleButtonError,
  transformedValuesForDonationRequest,
  formValues,
  setErrors,
}: ApplePaySubmitButtonProps) => {
  const [scriptReady, setScriptReady] = useState(false);
  const { t } = useTranslation();
  const {
    i18n: { language },
  } = useTranslation();

  const currentMountBtn = useRef('');

  const priceInCentsForRequest = getPriceInCents(totalAmount)!;

  const locale = buttonLocaleResolver[language as Language] || 'en-US';

  const [mountedPaymentIntentId, setMountedPaymentIntentId] = useState('');

  const {
    isValid: beckedValidationResult,
    isLoading,
    error: backendValidationError,
  } = useBackendValidation({
    enabled: isFormValid,
    formValues,
    transformData: transformedValuesForDonationRequest,
  });

  const isLoadingBackendValidation = isLoading && isFormValid;

  const { paymentIntent, isLoadingPaymentIntent, paymentIntentError } = useCreatePaymentIntent({
    projectId,
    totalAmountInCents: priceInCentsForRequest,
    language: locale,
    isFormValid: isFormValid && beckedValidationResult,
  });

  const { handlePaymentProcessed } = useHandlePaymentProcessed({
    onPaymentProcessError,
    formValues,
    transformedValuesForDonationRequest,
    mountedPaymentIntentId,
    paymentIntent,
  });

  useEffect(() => {
    if (paymentIntentError) {
      console.error('Failed to fetch payment intent:', paymentIntentError);
      onGenerateAppleButtonError(t('components.ApplePayButton.GenerateAppleButtonError'));
    }
  }, [paymentIntentError, onGenerateAppleButtonError, t]);

  const buttonType = isPlus ? 'contribute' : 'donate';

  useEffect(() => {
    loadTrustPayScript()
      .then(() => setScriptReady(true))
      .catch((err) => {
        console.error('TrustPay script loading failed:', err);
        onGenerateAppleButtonError(t('components.ApplePayButton.GenerateAppleButtonError'));
        captureException(new Error('Apple pay error: unable to load trust pay script'), {
          extra: { err },
        });
      });
  }, []);

  useDebouncedEffect(
    () => {
      const isFistLoad = currentMountBtn?.current === '';

      if (scriptReady && paymentIntent && !isLoadingPaymentIntent && (isFormValid || isFistLoad)) {
        const params: Omit<GenerateAppleButtonParams, 'handlePaymentProcessed'> = {
          secrets: {
            display: paymentIntent?.displaySecret,
            payment: paymentIntent?.paymentSecret,
          },
          appleInitResultData: paymentIntent?.appleInitResultData,
          buttonType,
          buttonLocale: locale,
        };

        const stringifyParams = JSON.stringify(params);

        if (currentMountBtn.current === stringifyParams && mountedPaymentIntentId === paymentIntent?.paymentIntentId) {
          console.info('Apple Pay button already generated, skipping re-initialization');
          return;
        }

        setMountedPaymentIntentId('');

        generateApplePayButton({
          ...params,
          handlePaymentProcessed: (result) => {
            setMountedPaymentIntentId(paymentIntent?.paymentIntentId || '');
            handlePaymentProcessed(result, paymentIntent?.donationIntentId);
          },
        })
          .catch((err) => {
            captureException(new Error('Apple pay error: unable to generate apple pay buttons'), {
              extra: { function: 'generateApplePayButton', err },
            });
            console.error('Failed to initialize Apple Pay:', err);
            onGenerateAppleButtonError(t('components.ApplePayButton.GenerateAppleButtonError'));
          })
          .then(() => {
            console.info('Apple Pay button generated successfully');
            currentMountBtn.current = stringifyParams;
            setMountedPaymentIntentId(paymentIntent?.paymentIntentId || '');
          });
      }
    },
    [scriptReady, paymentIntent?.paymentIntentId, isLoadingPaymentIntent, isFormValid],
    300,
  );

  if (isLoadingPaymentIntent) {
    return null;
  }

  const handleValidate = () => {
    if (!isFormValid) {
      submitForm();
      return;
    }

    if (isClientFetchError(backendValidationError)) {
      const validationsError = backendValidationError.body as unknown as ApiRequestValidationExceptionResponse;

      setErrors(transformCreateDonationViolationsToDonationFormErrors(validationsError.violations));
    }
  };

  const passesValidation = isFormValid && beckedValidationResult;
  const isPaymentIntentInvalid =
    paymentIntent?.totalAmountInCents !== priceInCentsForRequest ||
    mountedPaymentIntentId !== paymentIntent.paymentIntentId;

  const displayBlurBackground =
    isLoadingBackendValidation || (isPaymentIntentInvalid && isFormValid && passesValidation);

  return (
    <div className="h-[150px]">
      <div className="relative">
        {displayBlurBackground && (
          <div id="apple-pay-submit-button" className="absolute w-full h-full z-[2] bg-black opacity-15" />
        )}
        {!passesValidation && (
          <div id="apple-pay-submit-button" className="absolute w-full h-full z-[2]" onClick={handleValidate} />
        )}

        <div id={APPLE_PAY_BUTTON_ID}></div>
      </div>
    </div>
  );
};
